import { useCallback, useState } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { Title, usePermissions, type ListProps } from "react-admin";
import dayjs from "dayjs";
import { formatIsoDate } from "@/utils/date-format";
import type { PermissionRecord } from "@/types/permissions";
import { hasPermission } from "@/utils/permission-utils";
import DailyRosterSection from "./DailyRosterSection";
import DailySummarySection from "./DailySummarySection";
import ServiceDateControl from "./ServiceDateControl";

/** Combined daily roster + household delivery; stop-range CRUD lives on each FMB household Delivery tab. */
export default function FmbThaliDeliveryList(_props: ListProps) {
  const { permissions } = usePermissions<PermissionRecord>();
  const canRoster = hasPermission(permissions, "fmbThaliDistribution.view");
  const canDelivery = hasPermission(permissions, "fmbThaliDelivery.view");
  const [date, setDate] = useState(() => formatIsoDate(dayjs()));
  const [rosterHasMenu, setRosterHasMenu] = useState<boolean | null>(null);
  const [deliveryHasMenu, setDeliveryHasMenu] = useState<boolean | null>(null);

  const handleDateChange = useCallback((next: string) => {
    setDate(next);
    setRosterHasMenu(null);
    setDeliveryHasMenu(null);
  }, []);

  const onRosterHasDailyMenu = useCallback((has: boolean) => {
    setRosterHasMenu((prev) => (prev === has ? prev : has));
  }, []);

  const onDeliveryHasDailyMenu = useCallback((has: boolean) => {
    setDeliveryHasMenu((prev) => (prev === has ? prev : has));
  }, []);

  const rosterPending = canRoster && rosterHasMenu === null;
  const deliveryPending = canDelivery && deliveryHasMenu === null;
  const pagePending = rosterPending || deliveryPending;
  const noMenu = !pagePending && (rosterHasMenu === false || deliveryHasMenu === false);

  return (
    <Box sx={{ p: 2 }}>
      <Title title="Daily thalis" />
      <Box sx={{ mb: 2, maxWidth: 360 }}>
        <ServiceDateControl value={date} onChange={handleDateChange} />
      </Box>
      {pagePending ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 1.5 }}>
          Loading…
        </Typography>
      ) : null}
      {!pagePending && noMenu ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          No menu for this date — no deliveries.
        </Alert>
      ) : null}
      <Box sx={pagePending || noMenu ? { display: "none" } : undefined}>
        {canRoster ? (
          <DailyRosterSection date={date} onHasDailyMenu={onRosterHasDailyMenu} />
        ) : null}
        {canDelivery ? (
          <DailySummarySection date={date} onHasDailyMenu={onDeliveryHasDailyMenu} />
        ) : null}
      </Box>
    </Box>
  );
}
